import _ from "lodash";
import React, { useState, useMemo, useEffect } from "react";

// constants
import { ASSESSMENT, ELEMENT_TYPE_UNIT_PLAN } from "Constants/stringConstants";

// modules
import { getUnitPlanDetailsValueFromFieldKey } from "IBPlanner/modules/IBPlannerModule";

const SKIP_HOC_FOR_TYPES = [
  "SubjectGroupSelect",
  "FieldDropdown",
  "MeasureAssessing",
  "AddLearningGoals",
  "LearningGoalsNestedView",
  "LearningGoalsListView",
  // InputNumberList has issues with debounce
  "InputNumberList",
  "PlannerElementDataSet_Ported",
  "InputNumberList_Ported",
  "GenericCardList_Ported",
];

/**
 * @function withFieldComponentHOC
 * @param {React.Component} Component
 * @returns {React.Component}
 *
 * @description - this HOC will create a intermediate local state (value) for each field passed from the parent component (e.g. UnitPlan or Assessment) to the field component
 * it'll update the local state immediately and render it in the UI then it will update the parent component's state/cache after 1.5 seconds of user interaction.
 */

const withFieldComponentHOC = Component => {
  const WrappedComponent = props => {
    const {
      type,
      value,
      fieldKey,
      parentType,
      updateInputField,
      isMappedWithDetailsFields,
      field_list,
      debounceDuration = 1500,
    } = props;

    const [localValue, setLocalValue] = useState(value);

    // whenever cache value changes then sync it with local value
    useEffect(() => {
      if (!_.isEqual(value, localValue)) setLocalValue(value);
    }, [value]);

    const debouncedUpdateInputField = useMemo(() => {
      if (updateInputField)
        return _.debounce(updateInputField, debounceDuration);
      return updateInputField;
    }, []);

    // some components do not work for the HOC, skip it for them
    if (_.includes(SKIP_HOC_FOR_TYPES, type)) return <Component {...props} />;

    const localUpdateInputField = args => {
      const { extraParams = {}, params, isDebounce = false } = args || {};
      const { isMappedWithDetailsFields = false } = extraParams || {};

      if (isMappedWithDetailsFields) {
        setLocalValue(params);
      } else {
        setLocalValue(_.first(_.values(params)));
      }

      if (isDebounce) {
        // setting isDebounce to false because the function below is already debounced so no need to call debounced mutation
        debouncedUpdateInputField?.({ ...args, isDebounce: false });
      } else {
        updateInputField?.(args);
      }
    };

    switch (parentType) {
      case ASSESSMENT:
      case ELEMENT_TYPE_UNIT_PLAN:
      case "CREATE_UNIT_PLAN": {
        const getValue = () => {
          if (parentType === ASSESSMENT) {
            switch (fieldKey) {
              case "studentTemplate":
              case "openingMessageResource":
                return _.map(localValue, item => ({ ...item, isPost: true }));
              case "pedagogicalApproachDP": {
                if (_.every(localValue, item => _.has(item, "value"))) {
                  const selectedValues = _.filter(localValue, item =>
                    _.get(item, "isSelected", false)
                  );
                  return _.map(selectedValues, "id");
                }

                return localValue;
              }
              default:
                return localValue;
            }
          } else {
            // for fields like "dateDuration" where value will be like {startDate: "2023-10-10", endDate: "2023-10-20"} (combination for two or more fields)
            if (isMappedWithDetailsFields) {
              const mappedDetailsFields = _.get(
                field_list,
                `${fieldKey}.config.mappedDetailsFields`,
                []
              );

              return getUnitPlanDetailsValueFromFieldKey({
                unitPlanDetails: localValue,
                mappedDetailsFields,
                key: fieldKey,
              });
            } else {
              return _.has(localValue, "value")
                ? _.get(localValue, "value")
                : localValue;
            }
          }
        };

        const value = getValue();

        return (
          <Component
            {...props}
            value={value}
            updateInputField={localUpdateInputField}
          />
        );
      }
      default:
        return <Component {...props} />;
    }
  };

  return WrappedComponent;
};

export default withFieldComponentHOC;
